/**
 * WeeklyPayrollReportCard — Wochenuebersicht Krankheits-AUB/eAUB fuer die
 * Lohnabteilung. Standard: aktuelle Kalenderwoche (Mo–So).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { format, parseISO, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { de } from 'date-fns/locale';
import { Mail, ChevronLeft, ChevronRight, Download, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { absencesAPI } from '../../lib/api';
import { openAuthFile } from '../../lib/openAuthFile';
import { CERTIFICATE_TYPES, getCertificateType } from './constants';

const toIso = (d) => format(d, 'yyyy-MM-dd');

const buildWeek = (anchor) => {
  const monday = startOfWeek(anchor, { weekStartsOn: 1 });
  const sunday = addDays(monday, 6);
  return { start: toIso(monday), end: toIso(sunday) };
};

// KK-Gruppen — gleiches Schema wie CERTIFICATE_TYPES fuer einheitliche Anzeige
const KK_GROUPS = [
  { value: 'with_certificate', label: 'Kind krank mit Attest', short: 'KK + Attest',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' },
  { value: 'without_certificate', label: 'Kind krank ohne Attest', short: 'KK',
    color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
];

const exportCsv = (report) => {
  const rows = [
    ['Datum-Bereich', `${report.start_date} bis ${report.end_date}`],
    [],
    ['Kategorie', 'Mitarbeiter', 'Personalnr', 'Von', 'Bis', 'Tage', 'Attest-Datum', 'Notizen'],
  ];
  CERTIFICATE_TYPES.forEach((ct) => {
    const items = report.by_certificate?.[ct.value] || [];
    items.forEach((a) => {
      rows.push([
        `K · ${ct.short}`,
        a.employee_name,
        a.employee_number || '',
        a.start_date,
        a.end_date,
        a.days_count,
        a.doctor_note_date || '',
        (a.notes || '').replace(/[\r\n]+/g, ' '),
      ]);
    });
  });
  KK_GROUPS.forEach((kk) => {
    const items = report.kind_krank?.[kk.value] || [];
    items.forEach((a) => {
      rows.push([
        kk.short,
        a.employee_name,
        a.employee_number || '',
        a.start_date,
        a.end_date,
        a.days_count,
        '',
        (a.notes || '').replace(/[\r\n]+/g, ' '),
      ]);
    });
  });
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  // BOM for Excel UTF-8
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Krankheits-Report_${report.start_date}_bis_${report.end_date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const WeeklyPayrollReportCard = () => {
  const [anchor, setAnchor] = useState(new Date());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customMode, setCustomMode] = useState(false);

  const week = buildWeek(anchor);
  const startDate = customMode && customStart ? customStart : week.start;
  const endDate = customMode && customEnd ? customEnd : week.end;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await absencesAPI.getWeeklyPayrollReport(startDate, endDate);
      setReport(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden des Wochenreports');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const openAttachment = (att) => {
    openAuthFile(att.url, { filename: att.original_filename }).catch(() => {
      toast.error('Datei konnte nicht geöffnet werden');
    });
  };

  const counts = report?.counts || { AUB: 0, eAUB: 0, none: 0 };

  return (
    <Card className="border-border/60" data-testid="weekly-payroll-card">
      <CardHeader className="border-b border-border/40">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Wochen-Report Krankheit / Kind krank (Lohnabteilung)
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {!customMode && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAnchor((d) => subWeeks(d, 1))}
                  data-testid="payroll-week-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium tabular-nums px-2">
                  KW {format(parseISO(week.start), 'I', { locale: de })} ·{' '}
                  {format(parseISO(week.start), 'dd.MM.', { locale: de })}–
                  {format(parseISO(week.end), 'dd.MM.yyyy', { locale: de })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAnchor((d) => addWeeks(d, 1))}
                  data-testid="payroll-week-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCustomStart(week.start);
                    setCustomEnd(week.end);
                    setCustomMode(true);
                  }}
                  data-testid="payroll-custom-toggle"
                >
                  Eigener Zeitraum
                </Button>
              </>
            )}
            {customMode && (
              <>
                <DatePickerDE value={customStart} onChange={setCustomStart} placeholder="Von" />
                <span className="text-muted-foreground">–</span>
                <DatePickerDE value={customEnd} onChange={setCustomEnd} placeholder="Bis" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomMode(false)}
                  data-testid="payroll-custom-close"
                >
                  Wochenansicht
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => report && exportCsv(report)}
              disabled={!report || (report.total_count === 0)}
              data-testid="payroll-export-csv"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Counts — K-Bescheinigung */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Krankheit (K)
          </p>
          <div className="grid grid-cols-3 gap-3">
            {CERTIFICATE_TYPES.map((ct) => (
              <div
                key={ct.value}
                className="rounded-lg border border-border bg-secondary/20 p-3"
                data-testid={`payroll-count-${ct.value}`}
              >
                <Badge className={ct.color + ' mb-1.5'}>{ct.short}</Badge>
                <p className="text-2xl font-bold font-heading tabular-nums">
                  {counts[ct.value] || 0}
                </p>
                <p className="text-xs text-muted-foreground">{ct.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Counts — Kind krank */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Kind krank (KK)
          </p>
          <div className="grid grid-cols-2 gap-3">
            {KK_GROUPS.map((kk) => (
              <div
                key={kk.value}
                className="rounded-lg border border-border bg-secondary/20 p-3"
                data-testid={`payroll-count-kk-${kk.value}`}
              >
                <Badge className={kk.color + ' mb-1.5'}>{kk.short}</Badge>
                <p className="text-2xl font-bold font-heading tabular-nums">
                  {counts[`KK_${kk.value}`] || 0}
                </p>
                <p className="text-xs text-muted-foreground">{kk.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* List by category */}
        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Wird geladen…</p>
        ) : report?.total_count === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Keine Krank- oder Kind-krank-Meldungen in diesem Zeitraum.
          </p>
        ) : (
          <div className="space-y-3">
            {CERTIFICATE_TYPES.map((ct) => {
              const items = report?.by_certificate?.[ct.value] || [];
              if (items.length === 0) return null;
              return (
                <div key={ct.value} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Badge className={ct.color}>{ct.short}</Badge>
                    <span className="text-sm font-medium">{ct.label}</span>
                    <span className="text-xs text-muted-foreground">({items.length})</span>
                  </div>
                  <div className="rounded-md border border-border divide-y divide-border">
                    {items.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-3 px-3 py-2 text-sm"
                        data-testid={`payroll-row-${a.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{a.employee_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(a.start_date), 'dd.MM.', { locale: de })}–
                            {format(parseISO(a.end_date), 'dd.MM.yyyy', { locale: de })}
                            {a.employee_number && ` · ${a.employee_number}`}
                          </p>
                        </div>
                        <Badge variant="secondary" className="tabular-nums">{a.days_count} T</Badge>
                        {(a.attachments || []).length > 0 && (
                          <button
                            type="button"
                            onClick={() => openAttachment(a.attachments[0])}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-border bg-secondary/40 hover:bg-secondary"
                            title={a.attachments.map((x) => x.original_filename).join('\n')}
                            data-testid={`payroll-att-${a.id}`}
                          >
                            <Paperclip className="h-3 w-3" />
                            {a.attachments.length}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {KK_GROUPS.map((kk) => {
              const items = report?.kind_krank?.[kk.value] || [];
              if (items.length === 0) return null;
              return (
                <div key={`kk-${kk.value}`} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Badge className={kk.color}>{kk.short}</Badge>
                    <span className="text-sm font-medium">{kk.label}</span>
                    <span className="text-xs text-muted-foreground">({items.length})</span>
                  </div>
                  <div className="rounded-md border border-border divide-y divide-border">
                    {items.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-3 px-3 py-2 text-sm"
                        data-testid={`payroll-row-${a.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{a.employee_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(a.start_date), 'dd.MM.', { locale: de })}–
                            {format(parseISO(a.end_date), 'dd.MM.yyyy', { locale: de })}
                            {a.employee_number && ` · ${a.employee_number}`}
                          </p>
                        </div>
                        <Badge variant="secondary" className="tabular-nums">{a.days_count} T</Badge>
                        {(a.attachments || []).length > 0 && (
                          <button
                            type="button"
                            onClick={() => openAttachment(a.attachments[0])}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-border bg-secondary/40 hover:bg-secondary"
                            title={a.attachments.map((x) => x.original_filename).join('\n')}
                            data-testid={`payroll-att-${a.id}`}
                          >
                            <Paperclip className="h-3 w-3" />
                            {a.attachments.length}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
